(() => {
  'use strict';

  if (window.__oribrifGiftThresholdLoaded) return;
  window.__oribrifGiftThresholdLoaded = true;

  const loaderScript =
    document.currentScript ||
    document.querySelector('script[data-gift-threshold-starts-at][data-gift-threshold-ends-at]');

  const GIFT_CONFIG = Object.freeze({
    giftVariantId: 63842265923955,
    thresholdCents: Number(loaderScript?.dataset?.giftThresholdCents),
    giftName: '養髮液 20mL',
    giftPropertyKey: '_gift',
    giftCampaign: '',
    excludedVariantIds: [],
    startsAt: String(loaderScript?.dataset?.giftThresholdStartsAt ?? '').trim(),
    endsAt: String(loaderScript?.dataset?.giftThresholdEndsAt ?? '').trim(),
    enabled: true,
    maxActionsPerPage: 5,
  });

  const CAMPAIGN_WINDOW = parseCampaignWindow(GIFT_CONFIG);
  const MAX_TIMEOUT_MS = 2147483647;
  const OWN_REQUEST_HEADER = 'X-Oribrif-Gift-Sync';
  const SKIP_REQUEST_HEADER = 'X-Oribrif-Gift-Skip';
  const CART_MUTATION_PATH = /\/cart\/(?:add|change|update|clear)(?:\.js)?\/?$/;
  const CHECKOUT_CONTROL_SELECTOR =
    'button[name="checkout"], input[name="checkout"], #cart-checkout-btn, #CartDrawer-Checkout';
  const ACCELERATED_CHECKOUT_SELECTOR =
    '.cart__dynamic-checkout-buttons, #dynamic-checkout-cart, shopify-accelerated-checkout-cart';
  const originalFetch = window.fetch.bind(window);

  let isSyncing = false;
  let syncQueued = false;
  let syncChain = Promise.resolve();
  let pendingCartSnapshot = null;
  let forceFetchQueued = false;
  let actionCount = 0;
  let giftUnavailable = false;
  let warnedAboutActionLimit = false;
  let warnedAboutConfig = false;
  let warnedAboutCampaignWindow = false;
  let checkoutLockCount = 0;
  let checkoutSyncFailed = false;
  let campaignBoundaryTimer = null;

  function hasOwn(object, key) {
    return Object.prototype.hasOwnProperty.call(object || {}, key);
  }

  function parseCampaignTime(value) {
    const campaignTime = typeof value === 'string' ? value.trim() : '';
    if (!campaignTime) return { configured: false, valid: true, timestamp: null };

    const match = campaignTime.match(
      /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?(Z|([+-])(\d{2}):(\d{2}))$/
    );
    if (!match) return { configured: true, valid: false, timestamp: null };

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const hour = Number(match[4]);
    const minute = Number(match[5]);
    const second = Number(match[6] || 0);
    const millisecond = Number((match[7] || '').padEnd(3, '0'));
    const offsetHour = match[8] === 'Z' ? 0 : Number(match[10]);
    const offsetMinute = match[8] === 'Z' ? 0 : Number(match[11]);
    const isLeapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
    const daysPerMonth = [31, isLeapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    const componentsAreValid =
      year >= 1000 &&
      month >= 1 &&
      month <= 12 &&
      day >= 1 &&
      day <= daysPerMonth[month - 1] &&
      hour >= 0 &&
      hour <= 23 &&
      minute >= 0 &&
      minute <= 59 &&
      second >= 0 &&
      second <= 59 &&
      offsetHour >= 0 &&
      offsetHour <= 14 &&
      offsetMinute >= 0 &&
      offsetMinute <= 59 &&
      (offsetHour < 14 || offsetMinute === 0);

    if (!componentsAreValid) return { configured: true, valid: false, timestamp: null };

    const offsetDirection = match[9] === '-' ? -1 : 1;
    const offsetMilliseconds = offsetDirection * (offsetHour * 60 + offsetMinute) * 60 * 1000;
    const timestamp = Date.UTC(year, month - 1, day, hour, minute, second, millisecond) - offsetMilliseconds;

    return Number.isFinite(timestamp)
      ? { configured: true, valid: true, timestamp }
      : { configured: true, valid: false, timestamp: null };
  }

  function parseCampaignWindow(config = GIFT_CONFIG) {
    const start = parseCampaignTime(config?.startsAt);
    const end = parseCampaignTime(config?.endsAt);
    const chronological =
      !start.configured || !end.configured || (start.valid && end.valid && start.timestamp < end.timestamp);

    return {
      start,
      end,
      valid: start.valid && end.valid && chronological,
    };
  }

  function getCampaignWindow(config = GIFT_CONFIG) {
    return config === GIFT_CONFIG ? CAMPAIGN_WINDOW : parseCampaignWindow(config);
  }

  function getCampaignState(config = GIFT_CONFIG, nowMs = Date.now()) {
    const campaignWindow = getCampaignWindow(config);
    const currentTimestamp = Number(nowMs);

    if (!campaignWindow.valid || !Number.isFinite(currentTimestamp)) return 'INVALID';
    if (campaignWindow.start.configured && currentTimestamp < campaignWindow.start.timestamp) return 'UPCOMING';
    if (campaignWindow.end.configured && currentTimestamp >= campaignWindow.end.timestamp) return 'ENDED';
    return 'ACTIVE';
  }

  function isCampaignActive(config = GIFT_CONFIG, nowMs = Date.now()) {
    return getCampaignState(config, nowMs) === 'ACTIVE';
  }

  function getNextCampaignBoundary(config = GIFT_CONFIG, nowMs = Date.now()) {
    const campaignWindow = getCampaignWindow(config);
    const currentTimestamp = Number(nowMs);

    if (!campaignWindow.valid || !Number.isFinite(currentTimestamp)) return null;
    if (campaignWindow.start.configured && currentTimestamp < campaignWindow.start.timestamp) {
      return campaignWindow.start.timestamp;
    }
    if (campaignWindow.end.configured && currentTimestamp < campaignWindow.end.timestamp) {
      return campaignWindow.end.timestamp;
    }
    return null;
  }

  function isMarkedGift(item, config = GIFT_CONFIG) {
    return hasOwn(item?.properties, config.giftPropertyKey);
  }

  function isGiftVariantLine(item, config = GIFT_CONFIG) {
    return Number(item?.variant_id) === config.giftVariantId;
  }

  function isAuthorizedGiftLine(item, config = GIFT_CONFIG) {
    return isGiftVariantLine(item, config) && String(item?.properties?.[config.giftPropertyKey]) === 'true';
  }

  function calculateQualifyingSubtotal(cart, config = GIFT_CONFIG) {
    if (!cart || !Array.isArray(cart.items)) return 0;

    const excludedVariantIds = new Set(config.excludedVariantIds.map(Number));

    return cart.items.reduce((subtotal, item) => {
      const variantId = Number(item?.variant_id);
      const shouldExclude =
        variantId === config.giftVariantId ||
        excludedVariantIds.has(variantId) ||
        isMarkedGift(item, config) ||
        item?.gift_card === true;

      if (shouldExclude) return subtotal;

      const finalLinePrice = Number(item?.final_line_price);
      return Number.isFinite(finalLinePrice) ? subtotal + finalLinePrice : subtotal;
    }, 0);
  }

  function decideGiftAction(cart, config = GIFT_CONFIG, nowMs = Date.now()) {
    if (!cart || !Array.isArray(cart.items)) return { type: 'NONE' };

    const qualifyingSubtotal = calculateQualifyingSubtotal(cart, config);
    const giftVariantLines = cart.items.filter((item) => isGiftVariantLine(item, config));
    const unauthorizedGiftLine = giftVariantLines.find((item) => !isAuthorizedGiftLine(item, config) && item.key);

    if (unauthorizedGiftLine) {
      return { type: 'REMOVE', lineKey: unauthorizedGiftLine.key, quantity: 0, reason: 'UNAUTHORIZED' };
    }

    const authorizedGiftLines = giftVariantLines.filter((item) => isAuthorizedGiftLine(item, config));
    const giftLine = authorizedGiftLines[0];
    const duplicateGiftLine = authorizedGiftLines.slice(1).find((item) => item.key);

    if (duplicateGiftLine) {
      return { type: 'REMOVE', lineKey: duplicateGiftLine.key, quantity: 0, reason: 'DUPLICATE' };
    }

    const campaignState = getCampaignState(config, nowMs);
    if (campaignState !== 'ACTIVE') {
      if (giftLine?.key) {
        let reason = 'INVALID_SCHEDULE';
        if (campaignState === 'UPCOMING') reason = 'NOT_STARTED';
        if (campaignState === 'ENDED') reason = 'ENDED';
        return { type: 'REMOVE', lineKey: giftLine.key, quantity: 0, reason };
      }
      return { type: 'NONE' };
    }

    if (qualifyingSubtotal >= config.thresholdCents) {
      if (!giftLine) return { type: 'ADD' };
      if (Number(giftLine.quantity) !== 1 && giftLine.key) {
        return { type: 'CHANGE', lineKey: giftLine.key, quantity: 1 };
      }
      return { type: 'NONE' };
    }

    if (giftLine?.key) return { type: 'REMOVE', lineKey: giftLine.key, quantity: 0 };
    return { type: 'NONE' };
  }

  function isValidConfig(config = GIFT_CONFIG) {
    return (
      config.enabled === true &&
      Number.isSafeInteger(config.giftVariantId) &&
      config.giftVariantId > 0 &&
      Number.isSafeInteger(config.thresholdCents) &&
      config.thresholdCents > 0
    );
  }

  function isValidCart(cart) {
    return cart && typeof cart === 'object' && Array.isArray(cart.items);
  }

  function isCompleteCartSnapshot(cart) {
    return isValidCart(cart) && Number.isInteger(cart.item_count);
  }

  function getCheckoutStatusHosts() {
    const hosts = new Set();

    document.querySelectorAll(CHECKOUT_CONTROL_SELECTOR).forEach((control) => {
      const host = control.closest('.cart__ctas, #cart-notification-form') || control.parentElement;
      if (host) hosts.add(host);
    });

    return [...hosts];
  }

  function updateCheckoutStatus(locked) {
    const message = checkoutSyncFailed
      ? '購物車同步失敗，請重新整理頁面後再結帳。'
      : '正在同步購物車，請稍候…';

    getCheckoutStatusHosts().forEach((host) => {
      let status = host.querySelector('[data-gift-threshold-checkout-status]');

      if (!locked) {
        status?.remove();
        return;
      }

      if (!status) {
        status = document.createElement('p');
        status.className = 'gift-threshold-checkout-status';
        status.dataset.giftThresholdCheckoutStatus = '';
        status.setAttribute('role', checkoutSyncFailed ? 'alert' : 'status');
        status.setAttribute('aria-live', checkoutSyncFailed ? 'assertive' : 'polite');
        host.append(status);
      }

      status.textContent = message;
      status.setAttribute('role', checkoutSyncFailed ? 'alert' : 'status');
      status.setAttribute('aria-live', checkoutSyncFailed ? 'assertive' : 'polite');
    });
  }

  function applyCheckoutLockState() {
    const locked = checkoutLockCount > 0 || checkoutSyncFailed;
    document.documentElement.classList.toggle('gift-threshold-checkout-locked', locked);

    document.querySelectorAll(CHECKOUT_CONTROL_SELECTOR).forEach((control) => {
      if (locked) {
        if (!hasOwn(control.dataset, 'giftThresholdWasDisabled')) {
          control.dataset.giftThresholdWasDisabled = control.disabled ? 'true' : 'false';
        }
        control.disabled = true;
        control.setAttribute('aria-busy', 'true');
        return;
      }

      if (control.dataset.giftThresholdWasDisabled === 'false') control.disabled = false;
      delete control.dataset.giftThresholdWasDisabled;
      control.removeAttribute('aria-busy');
    });

    document.querySelectorAll(ACCELERATED_CHECKOUT_SELECTOR).forEach((container) => {
      if (locked) {
        if (!hasOwn(container.dataset, 'giftThresholdWasInert')) {
          container.dataset.giftThresholdWasInert = container.hasAttribute('inert') ? 'true' : 'false';
        }
        container.setAttribute('inert', '');
        container.setAttribute('aria-disabled', 'true');
        container.setAttribute('aria-busy', 'true');
        return;
      }

      if (container.dataset.giftThresholdWasInert === 'false') container.removeAttribute('inert');
      delete container.dataset.giftThresholdWasInert;
      container.removeAttribute('aria-disabled');
      container.removeAttribute('aria-busy');
    });

    updateCheckoutStatus(locked);
  }

  function acquireCheckoutLock() {
    checkoutLockCount += 1;
    applyCheckoutLockState();

    let released = false;
    return () => {
      if (released) return;
      released = true;
      checkoutLockCount = Math.max(0, checkoutLockCount - 1);
      applyCheckoutLockState();
    };
  }

  function clearCheckoutSyncFailure() {
    if (!checkoutSyncFailed) return;
    checkoutSyncFailed = false;
    applyCheckoutLockState();
  }

  function markCheckoutSyncFailure() {
    checkoutSyncFailed = true;
    applyCheckoutLockState();
  }

  function isCheckoutIntent(event) {
    const submitter = event.submitter;
    if (submitter?.matches?.(CHECKOUT_CONTROL_SELECTOR)) return true;

    if (event.type === 'click') {
      return Boolean(event.target?.closest?.(`${CHECKOUT_CONTROL_SELECTOR}, ${ACCELERATED_CHECKOUT_SELECTOR}`));
    }

    const form = event.target;
    return form instanceof HTMLFormElement && Boolean(form.querySelector(CHECKOUT_CONTROL_SELECTOR));
  }

  function preventCheckoutWhileLocked(event) {
    if ((checkoutLockCount === 0 && !checkoutSyncFailed) || !isCheckoutIntent(event)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    updateCheckoutStatus(true);
  }

  function getRootRoute() {
    const root = window.Shopify?.routes?.root ?? '/';
    return root.endsWith('/') ? root : `${root}/`;
  }

  function getCartUrl(path) {
    return `${getRootRoute()}cart${path}`;
  }

  function getSectionDescriptors() {
    const cartItems = document.querySelector('cart-items');
    if (!cartItems || typeof cartItems.getSectionsToRender !== 'function') return [];

    return cartItems
      .getSectionsToRender()
      .filter((section) => section?.section && document.getElementById(section.id))
      .slice(0, 5);
  }

  function getRequestSections() {
    return [...new Set(getSectionDescriptors().map((section) => section.section))];
  }

  async function parseJson(response) {
    try {
      return await response.json();
    } catch (_error) {
      return null;
    }
  }

  async function fetchCart() {
    try {
      const response = await window.fetch(getCartUrl('.js'), {
        headers: {
          Accept: 'application/json',
          [OWN_REQUEST_HEADER]: 'true',
        },
      });

      if (!response.ok) return null;
      const cart = await parseJson(response);
      return isCompleteCartSnapshot(cart) ? cart : null;
    } catch (_error) {
      return null;
    }
  }

  function buildGiftProperties() {
    const properties = {
      [GIFT_CONFIG.giftPropertyKey]: 'true',
    };

    if (GIFT_CONFIG.giftCampaign) properties._gift_campaign = GIFT_CONFIG.giftCampaign;
    return properties;
  }

  async function performAction(action) {
    if (actionCount >= GIFT_CONFIG.maxActionsPerPage) {
      if (!warnedAboutActionLimit) {
        console.warn('[Gift threshold] 已達單頁動作上限，停止修改購物車。');
        warnedAboutActionLimit = true;
      }
      return { status: 'limit' };
    }

    actionCount += 1;

    const sections = getRequestSections();
    const sharedPayload = {
      sections,
      sections_url: window.location.pathname,
    };
    let url;
    let payload;

    if (action.type === 'ADD') {
      url = getCartUrl('/add.js');
      payload = {
        items: [
          {
            id: GIFT_CONFIG.giftVariantId,
            quantity: 1,
            properties: buildGiftProperties(),
          },
        ],
        ...sharedPayload,
      };
    } else {
      url = getCartUrl('/change.js');
      payload = {
        id: action.lineKey,
        quantity: action.quantity,
        ...sharedPayload,
      };
    }

    try {
      const response = await window.fetch(url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          [OWN_REQUEST_HEADER]: 'true',
        },
        body: JSON.stringify(payload),
      });

      if (action.type === 'ADD' && response.status === 422) {
        giftUnavailable = true;
        console.warn('[Gift threshold] 贈品目前無法加入，可能已缺貨。');
        return { status: 'unavailable' };
      }

      if (!response.ok) return { status: 'failed' };
      return { status: 'success', data: await parseJson(response) };
    } catch (_error) {
      return { status: 'failed' };
    }
  }

  function renderSections(sections) {
    if (!sections || typeof sections !== 'object') return false;

    const updates = [];
    for (const descriptor of getSectionDescriptors()) {
      const html = sections[descriptor.section];
      const targetRoot = document.getElementById(descriptor.id);
      if (typeof html !== 'string' || !targetRoot) return false;

      const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
      const source = parsedDocument.querySelector(descriptor.selector);
      const target = targetRoot.querySelector(descriptor.selector) || targetRoot;
      if (!source || !target) return false;

      updates.push({ target, html: source.innerHTML });
    }

    if (updates.length === 0) return false;
    updates.forEach((update) => {
      update.target.innerHTML = update.html;
    });
    return true;
  }

  function toggleEmptyState(cart) {
    if (!isValidCart(cart)) return;

    const isEmpty = Number(cart.item_count) === 0;
    document.querySelector('cart-items')?.classList.toggle('is-empty', isEmpty);
    document.getElementById('main-cart-footer')?.classList.toggle('is-empty', isEmpty);
  }

  function createGiftPrice(price, compareAtPrice) {
    const wrapper = document.createElement('span');
    wrapper.className = 'gift-threshold-line-price';

    if (compareAtPrice && compareAtPrice !== price) {
      const compareAt = document.createElement('s');
      compareAt.className = 'gift-threshold-line-price__compare';
      compareAt.textContent = compareAtPrice;
      wrapper.append(compareAt);
    }

    const current = document.createElement('strong');
    current.className = 'gift-threshold-line-price__current';
    current.textContent = price || 'NT$0';
    wrapper.append(current);
    return wrapper;
  }

  function findDirectPriceSource(element) {
    return [...element.children].find(
      (child) => child.matches('.cart-item__discounted-prices, .product-option, .price') && !child.matches('unit-price')
    );
  }

  function decorateGiftLine(row) {
    if (row.dataset.giftThresholdDecorated === 'true') return;

    row.dataset.giftThresholdDecorated = 'true';
    row.classList.add('cart-item--gift-threshold');

    const productName = row.querySelector('.cart-item__name');
    if (productName && !row.querySelector('.gift-threshold-badge')) {
      const badge = document.createElement('span');
      badge.className = 'gift-threshold-badge';
      badge.textContent = '贈品';
      productName.insertAdjacentElement('afterend', badge);
    }

    const quantityWrapper = row.querySelector('.cart-item__quantity-wrapper');
    if (quantityWrapper && !quantityWrapper.querySelector('.gift-threshold-fixed-quantity')) {
      const fixedQuantity = document.createElement('span');
      fixedQuantity.className = 'gift-threshold-fixed-quantity';
      fixedQuantity.textContent = '1';
      fixedQuantity.setAttribute('aria-label', '贈品數量 1');
      quantityWrapper.prepend(fixedQuantity);
    }

    const price = row.dataset.giftThresholdPrice || 'NT$0';
    const compareAtPrice = row.dataset.giftThresholdCompareAtPrice || '';
    const details = row.querySelector('.cart-item__details');
    const detailsPriceSource = details ? findDirectPriceSource(details) : null;

    if (detailsPriceSource && !details.querySelector('.gift-threshold-line-price')) {
      detailsPriceSource.classList.add('gift-threshold-price-source');
      detailsPriceSource.insertAdjacentElement('afterend', createGiftPrice(price, compareAtPrice));
    }

    row.querySelectorAll('.cart-item__price-wrapper').forEach((priceWrapper) => {
      if (priceWrapper.querySelector('.gift-threshold-line-price')) return;
      const source = findDirectPriceSource(priceWrapper);
      if (source) source.classList.add('gift-threshold-price-source');
      priceWrapper.append(createGiftPrice(price, compareAtPrice));
    });
  }

  function decorateGiftLines() {
    document.querySelectorAll('[data-gift-threshold-line="true"]').forEach(decorateGiftLine);
  }

  function formatTwd(cents) {
    const amount = Math.max(0, Math.ceil(Number(cents) / 100));
    return `NT$${new Intl.NumberFormat('zh-TW', { maximumFractionDigits: 0 }).format(amount)}`;
  }

  function updateProgressUi(cart) {
    const container = document.querySelector('[data-gift-threshold-ui]');
    if (!container || !isValidCart(cart)) return;

    if (!isCampaignActive()) {
      giftUnavailable = false;
      container.hidden = true;
      return;
    }

    const subtotal = calculateQualifyingSubtotal(cart);
    const remaining = Math.max(0, GIFT_CONFIG.thresholdCents - subtotal);
    const percent = Math.min(100, Math.max(0, (subtotal / GIFT_CONFIG.thresholdCents) * 100));
    const hasGift = cart.items.some((item) => isAuthorizedGiftLine(item));
    const message = container.querySelector('[data-gift-threshold-message]');
    const progress = container.querySelector('[data-gift-threshold-progress]');
    const fill = container.querySelector('[data-gift-threshold-fill]');

    if (subtotal < GIFT_CONFIG.thresholdCents) {
      giftUnavailable = false;
      message.textContent = `折扣後再購 ${formatTwd(remaining)} 即可獲得${GIFT_CONFIG.giftName}`;
    } else if (giftUnavailable && !hasGift) {
      message.textContent = '贈品目前補貨中，我們會盡快補上';
    } else if (hasGift) {
      message.textContent = `已達標，${GIFT_CONFIG.giftName}已加入購物車 🎁`;
    } else {
      message.textContent = `已達標，正在加入${GIFT_CONFIG.giftName}…`;
    }

    fill.style.width = `${percent}%`;
    progress.setAttribute('aria-valuenow', String(Math.round(percent)));
    container.hidden = false;
  }

  async function synchronizeGift(cartSnapshot = null) {
    if (isSyncing || !isValidConfig()) {
      if (!isValidConfig() && !warnedAboutConfig) {
        console.warn('[Gift threshold] 設定無效或功能已停用。');
        warnedAboutConfig = true;
      }
      return isValidConfig();
    }

    if (!CAMPAIGN_WINDOW.valid && !warnedAboutCampaignWindow) {
      console.warn(
        '[Gift threshold] 活動時間設定無效，贈品將保持停用。請使用含時區的 ISO 8601 格式，並確認結束時間晚於開始時間。'
      );
      warnedAboutCampaignWindow = true;
    }

    isSyncing = true;

    try {
      const cart = isCompleteCartSnapshot(cartSnapshot) ? cartSnapshot : await fetchCart();
      if (!isValidCart(cart)) return false;

      decorateGiftLines();
      updateProgressUi(cart);

      const action = decideGiftAction(cart);
      if (action.type === 'NONE') return true;
      if (action.type === 'ADD' && giftUnavailable) return true;

      const result = await performAction(action);
      if (result.status === 'unavailable') {
        updateProgressUi(cart);
        return true;
      }
      if (result.status !== 'success') return false;

      const rendered = renderSections(result.data?.sections);
      if (!rendered) {
        window.location.reload();
        return true;
      }

      // Multi-item add.js returns only the lines added in this request, not a full cart snapshot.
      // Always read cart.js after ADD so progress and gift state use the complete cart.
      const updatedCart =
        action.type === 'ADD' || !isCompleteCartSnapshot(result.data) ? await fetchCart() : result.data;
      if (!isValidCart(updatedCart)) return false;
      toggleEmptyState(updatedCart);
      decorateGiftLines();
      updateProgressUi(updatedCart);
      applyCheckoutLockState();

      // Only continue when another corrective action is actually necessary.
      // Reuse the full cart returned by change.js instead of fetching cart.js again.
      const nextAction = decideGiftAction(updatedCart);
      if (nextAction.type !== 'NONE' && !(nextAction.type === 'ADD' && giftUnavailable)) {
        scheduleSync(updatedCart);
      }
      return true;
    } finally {
      isSyncing = false;
    }
  }

  function scheduleSync(cartSnapshot = null, options = {}) {
    if (options.forceFetch === true) {
      forceFetchQueued = true;
      pendingCartSnapshot = null;
    } else if (!forceFetchQueued && isCompleteCartSnapshot(cartSnapshot)) {
      pendingCartSnapshot = cartSnapshot;
    }
    if (syncQueued) return syncChain;

    clearCheckoutSyncFailure();
    const releaseCheckout = acquireCheckoutLock();
    syncQueued = true;
    syncChain = syncChain
      .then(async () => {
        syncQueued = false;
        const queuedCartSnapshot = forceFetchQueued ? null : pendingCartSnapshot;
        forceFetchQueued = false;
        pendingCartSnapshot = null;
        const synchronized = await synchronizeGift(queuedCartSnapshot);
        if (!synchronized) markCheckoutSyncFailure();
        return synchronized;
      })
      .catch((error) => {
        syncQueued = false;
        isSyncing = false;
        markCheckoutSyncFailure();
        console.error('[Gift threshold] 購物車同步失敗。', error);
        return false;
      })
      .finally(releaseCheckout);

    return syncChain;
  }

  function scheduleCampaignBoundary() {
    if (campaignBoundaryTimer !== null) {
      window.clearTimeout(campaignBoundaryTimer);
      campaignBoundaryTimer = null;
    }

    if (!CAMPAIGN_WINDOW.valid) return;

    const nowMs = Date.now();
    const nextBoundary = getNextCampaignBoundary(GIFT_CONFIG, nowMs);

    if (nextBoundary === null) return;

    const delay = Math.min(Math.max(0, nextBoundary - nowMs), MAX_TIMEOUT_MS);
    campaignBoundaryTimer = window.setTimeout(() => {
      campaignBoundaryTimer = null;

      if (Date.now() >= nextBoundary) {
        // A cart page can remain open for the whole campaign. Reset the loop guard
        // and fetch a fresh cart so start/end cleanup cannot use a stale snapshot.
        actionCount = 0;
        scheduleSync(null, { forceFetch: true });
      }
      scheduleCampaignBoundary();
    }, delay);
  }

  async function whenStable(cartSnapshot = null) {
    await scheduleSync(cartSnapshot);

    let observedChain;
    do {
      observedChain = syncChain;
      await observedChain;
    } while (observedChain !== syncChain || syncQueued || isSyncing);

    applyCheckoutLockState();
    return !checkoutSyncFailed;
  }

  function requestHasHeader(input, init, headerName) {
    try {
      const initHeaders = init?.headers ? new Headers(init.headers) : null;
      if (initHeaders?.get(headerName) === 'true') return true;

      return input instanceof Request && input.headers.get(headerName) === 'true';
    } catch (_error) {
      return false;
    }
  }

  function hasOwnRequestHeader(input, init) {
    return requestHasHeader(input, init, OWN_REQUEST_HEADER);
  }

  function shouldSkipRequest(input, init) {
    return requestHasHeader(input, init, SKIP_REQUEST_HEADER);
  }

  function isCartMutationRequest(input) {
    try {
      const requestUrl = input instanceof Request ? input.url : input;
      return CART_MUTATION_PATH.test(new URL(requestUrl, window.location.origin).pathname);
    } catch (_error) {
      return false;
    }
  }

  window.fetch = function giftThresholdFetchInterceptor(input, init) {
    const shouldSynchronize =
      isCartMutationRequest(input) && !hasOwnRequestHeader(input, init) && !shouldSkipRequest(input, init);
    if (!shouldSynchronize) return originalFetch(input, init);

    const releaseCheckout = acquireCheckoutLock();

    return originalFetch(input, init).then(
      (response) => {
        if (!response.ok) {
          releaseCheckout();
          return response;
        }

        const cartSnapshotPromise = parseJson(response.clone());

        // Let the cart component finish its own render before gift reconciliation.
        window.setTimeout(() => {
          cartSnapshotPromise
            .then((cartSnapshot) => scheduleSync(cartSnapshot))
            .finally(releaseCheckout);
        }, 0);
        return response;
      },
      (error) => {
        releaseCheckout();
        throw error;
      }
    );
  };

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      scheduleCampaignBoundary();
      actionCount = 0;
      scheduleSync(null, { forceFetch: true });
    }
  });

  function initialize() {
    document.addEventListener('click', preventCheckoutWhileLocked, true);
    document.addEventListener('submit', preventCheckoutWhileLocked, true);
    decorateGiftLines();
    scheduleCampaignBoundary();
    scheduleSync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  window.OribrifGiftThreshold = Object.freeze({
    config: GIFT_CONFIG,
    calculateQualifyingSubtotal,
    decideGiftAction,
    getCampaignState,
    getNextCampaignBoundary,
    isCampaignActive,
    isAuthorizedGiftLine,
    isCompleteCartSnapshot,
    lockCheckout: acquireCheckoutLock,
    parseCampaignTime,
    parseCampaignWindow,
    scheduleSync,
    whenStable,
  });
})();
